import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ResolvedBranding,
  applyBranding,
  resolveBranding,
} from '../utils/whitelabel';
import { useTheme } from './ThemeContext';
import { useUserContext } from './UserContext';

interface BrandingContextValue extends ResolvedBranding {
  /** Wordmark for the active theme — use this instead of branching on theme. */
  logoSrc: string;
  /** Square mark for the active theme. */
  iconSrc: string;
  setPageTitle: (title?: string) => void;
  setItemTitle: (level: ItemTitleLevel, title?: string) => void;
}

/** An editor's item is more specific than the screen's, so it wins while open. */
type ItemTitleLevel = 'screen' | 'editor';

const BrandingContext = createContext<BrandingContextValue | undefined>(
  undefined,
);

export function BrandingContextProvider({ children }: { children: ReactNode }) {
  const { selectedAccount } = useUserContext();
  const { resolvedTheme } = useTheme();
  const [pageTitle, setPageTitle] = useState<string>();
  const [itemTitles, setItemTitles] = useState<
    Partial<Record<ItemTitleLevel, string>>
  >({});

  const setItemTitle = useCallback(
    (level: ItemTitleLevel, title?: string) =>
      setItemTitles((titles) => ({ ...titles, [level]: title })),
    [],
  );

  const branding = useMemo(
    () => resolveBranding(selectedAccount?.data().whitelabel),
    [selectedAccount],
  );

  useEffect(() => {
    applyBranding(branding, resolvedTheme);
  }, [branding, resolvedTheme]);

  useEffect(() => {
    const itemTitle = itemTitles.editor || itemTitles.screen;
    document.title = [itemTitle, pageTitle, branding.appName]
      .filter(Boolean)
      .join(' | ');
  }, [itemTitles, pageTitle, branding.appName]);

  const value = useMemo(
    () => ({
      ...branding,
      logoSrc:
        resolvedTheme === 'dark' ? branding.logoOnDarkUrl : branding.logoUrl,
      iconSrc:
        resolvedTheme === 'dark' ? branding.iconOnDarkUrl : branding.iconUrl,
      setPageTitle,
      setItemTitle,
    }),
    [branding, resolvedTheme, setItemTitle],
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);

  if (!context) {
    throw new Error(
      'useBranding must be used within a BrandingContextProvider',
    );
  }

  return context;
}

/** Names the open page, variant, quiz or form in the browser tab title. */
export function useItemTitle(
  title: string | undefined,
  level: ItemTitleLevel = 'screen',
) {
  const { setItemTitle } = useBranding();

  useEffect(() => {
    setItemTitle(level, title);
    return () => setItemTitle(level, undefined);
  }, [level, title, setItemTitle]);
}
